import { useLocation, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { ActivityJson } from 'app/types/entities'
import { useActivityStore } from 'app/stores/StoreContext'
import { notifyError } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'

/** Manages focused activity, drawer open/close state, and URL `?drawer=` param sync. */
export function useActivityDrawer(debugLabel = `activities`) {
  const store = useActivityStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const baseUrl = useMemo(() => {
    const params = new URLSearchParams(location.search)
    params.delete(`drawer`)
    const search = params.toString()
    return `${location.pathname}${search ? `?${search}` : ``}`
  }, [location.pathname, location.search])

  const [focusedActivity, setFocusedActivity] = useState<ActivityJson>()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [isActivityLoading, setIsActivityLoading] = useState(false)

  const drawerId = searchParams.get(`drawer`)

  const loadActivityById = (activityId: string) => {
    setIsActivityLoading(true)
    setOpenDrawer(true)
    bg(async () => {
      try {
        const result = await store.fetchOne(activityId)
        debug(debugLabel, `data`, result.data)
        const activity = (result.data as { activity?: ActivityJson } | undefined)?.activity
        if (result.error || !activity) {
          const message = result.error?.message ?? `not found`
          logError(`Error fetching activity`, message)
          notifyError(`Activity ${message}`)
          setFocusedActivity(undefined)
          setOpenDrawer(false)
        } else {
          setFocusedActivity(activity)
          setOpenDrawer(true)
        }
      } finally {
        setIsActivityLoading(false)
      }
    })
  }

  // Restore drawer from URL (deep link / refresh, and a navigation from the command palette
  // into an already-mounted page). Compare against the focused activity, not `openDrawer`:
  // a new id arriving while a drawer is open has to replace what is on screen. Open
  // immediately so loading state is visible.
  useEffect(() => {
    if (drawerId && drawerId !== `` && focusedActivity?.id !== drawerId) {
      loadActivityById(drawerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirror ContactsTable: only react to drawer id changes
  }, [drawerId])

  const selectActivity = (activity: ActivityJson) => {
    if (focusedActivity?.id === activity.id) return
    debug(debugLabel, `activityHandler`, activity, activity.typeId)
    setFocusedActivity(activity)
    setOpenDrawer(true)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set(`drawer`, activity.id!)
      return next
    })
  }

  const closeDrawer = () => {
    setFocusedActivity(undefined)
    setOpenDrawer(false)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete(`drawer`)
      return next
    })
  }

  return {
    focusedActivity,
    setFocusedActivity,
    openDrawer,
    setOpenDrawer,
    isActivityLoading,
    baseUrl,
    selectActivity,
    closeDrawer,
  }
}
